/**
 * Supplier module DTOs and presentation helpers.
 */

export enum SupplierStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  SUSPENDED = 'suspended',
  INACTIVE = 'inactive',
}

export enum SupplierUserRole {
  OWNER = 'owner',
  ADMIN = 'admin',
  MANAGER = 'manager',
  OPERATOR = 'operator',
  VIEWER = 'viewer',
}

export enum SupplierProductStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  OUT_OF_STOCK = 'out_of_stock',
  DISCONTINUED = 'discontinued',
  PENDING = 'pending',
  REJECTED = 'rejected',
}

export interface RussianBusinessDetails {
  inn: string
  kpp?: string
  ogrn?: string
  legalName: string
  legalAddress?: string
}

export interface ContactInfo {
  email: string
  phone?: string
  address?: string
  website?: string
  contactPerson?: string
  contactPosition?: string
}

export interface Supplier {
  id: string
  businessDetails: RussianBusinessDetails
  contactInfo: ContactInfo
  status: SupplierStatus
  rating: number
  reviewCount: number
  description?: string
  additionalInfo?: Record<string, unknown>
  isActive: boolean
  isFeatured: boolean
  commissionRate?: number
  createdAt: string
  updatedAt?: string
  approvedAt?: string
  moderationNotes?: string
  activeProductCount?: number
  totalProductCount?: number
}

export interface SupplierUser {
  id: string
  supplier: Supplier
  user: {
    id: string
    firstName: string
    lastName: string
    email: string
    phone?: string
  }
  role: SupplierUserRole
  position?: string
  workPhone?: string
  department?: string
  permissions?: string[]
  isActive: boolean
  canManageProducts: boolean
  canManageOrders: boolean
  canManageUsers: boolean
  canViewReports: boolean
  canManageProfile: boolean
  notes?: string
  createdAt: string
  updatedAt: string
  lastActiveAt?: string
  invitedAt?: string
  joinedAt?: string
}

export interface SupplierProduct {
  id: string
  supplier: Supplier
  product: {
    id: string
    name: string
    sku: string
    categoryName: string
    manufacturerName: string
    imageUrl?: string
  }
  supplierSku?: string
  supplierPrice: {
    amount: number
    currency: string
  }
  discountPrice?: {
    amount: number
    currency: string
  }
  stock: number
  minimumOrder: number
  status: SupplierProductStatus
  deliveryTime: number
  supplierNotes?: string
  moderationNotes?: string
  createdAt: string
  updatedAt: string
  priceUpdatedAt?: string
  stockUpdatedAt?: string
}

export interface CreateSupplierData {
  businessDetails: RussianBusinessDetails
  contactInfo: ContactInfo
  description?: string
}

export interface UpdateSupplierData {
  businessDetails?: Partial<RussianBusinessDetails>
  contactInfo?: Partial<ContactInfo>
  description?: string
  additionalInfo?: Record<string, unknown>
  commissionRate?: number
}

export interface CreateSupplierProductData {
  productId: string
  supplierSku?: string
  supplierPrice: {
    amount: number
    currency: string
  }
  discountPrice?: {
    amount: number
    currency: string
  }
  stock: number
  minimumOrder?: number
  deliveryTime?: number
  supplierNotes?: string
}

export interface UpdateSupplierProductData {
  supplierSku?: string
  supplierPrice?: {
    amount: number
    currency: string
  }
  discountPrice?: {
    amount: number
    currency: string
  }
  stock?: number
  minimumOrder?: number
  status?: SupplierProductStatus
  deliveryTime?: number
  supplierNotes?: string
}

export interface SuppliersResponse {
  suppliers: Supplier[]
  pagination: {
    total: number
    page: number
    limit: number
    pages: number
  }
}

export interface SupplierStats {
  totalSuppliers: number
  activeSuppliers: number
  pendingSuppliers: number
  rejectedSuppliers: number
  averageRating: number
  totalProducts: number
  totalOrders: number
  totalRevenue: number
}

export const SupplierStatusLabels: Record<SupplierStatus, string> = {
  [SupplierStatus.PENDING]: 'Ожидает модерации',
  [SupplierStatus.APPROVED]: 'Одобрен',
  [SupplierStatus.REJECTED]: 'Отклонен',
  [SupplierStatus.SUSPENDED]: 'Приостановлен',
  [SupplierStatus.INACTIVE]: 'Неактивен',
}

export const SupplierUserRoleLabels: Record<SupplierUserRole, string> = {
  [SupplierUserRole.OWNER]: 'Владелец',
  [SupplierUserRole.ADMIN]: 'Администратор',
  [SupplierUserRole.MANAGER]: 'Менеджер',
  [SupplierUserRole.OPERATOR]: 'Оператор',
  [SupplierUserRole.VIEWER]: 'Наблюдатель',
}

export const SupplierProductStatusLabels: Record<SupplierProductStatus, string> = {
  [SupplierProductStatus.ACTIVE]: 'Активен',
  [SupplierProductStatus.INACTIVE]: 'Неактивен',
  [SupplierProductStatus.OUT_OF_STOCK]: 'Нет в наличии',
  [SupplierProductStatus.DISCONTINUED]: 'Снят с производства',
  [SupplierProductStatus.PENDING]: 'Ожидает модерации',
  [SupplierProductStatus.REJECTED]: 'Отклонен',
}

export function getSupplierStatusColor(status: SupplierStatus): string {
  switch (status) {
    case SupplierStatus.APPROVED:
      return 'success'
    case SupplierStatus.PENDING:
      return 'warning'
    case SupplierStatus.REJECTED:
      return 'error'
    case SupplierStatus.SUSPENDED:
      return 'orange'
    case SupplierStatus.INACTIVE:
      return 'grey'
    default:
      return 'grey'
  }
}

export function getSupplierProductStatusColor(status: SupplierProductStatus): string {
  switch (status) {
    case SupplierProductStatus.ACTIVE:
      return 'success'
    case SupplierProductStatus.PENDING:
      return 'warning'
    case SupplierProductStatus.OUT_OF_STOCK:
      return 'orange'
    case SupplierProductStatus.REJECTED:
      return 'error'
    case SupplierProductStatus.DISCONTINUED:
      return 'grey'
    case SupplierProductStatus.INACTIVE:
      return 'grey'
    default:
      return 'grey'
  }
}
